const MAGNIFIER_ID = "incluway-content-magnifier";
const WIDGET_HOST_ID = "incluway-widget-root";

let magnifierEl: HTMLDivElement | null = null;

let docMouseMoveHandler: EventListener | null = null;
let docMouseOverHandler: EventListener | null = null;

let widgetMouseMoveHandler: EventListener | null = null;
let widgetMouseOverHandler: EventListener | null = null;

const ALLOWED_SELECTOR = [
  "p",
  "span",
  "a",
  "li",
  "label",
  "button",
  "td",
  "th",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "small",
  "strong",
].join(",");

function getWidgetShadowRoot(): ShadowRoot | null {
  return document.getElementById(WIDGET_HOST_ID)?.shadowRoot ?? null;
}

function createMagnifier() {
  if (magnifierEl) return;

  magnifierEl = document.createElement("div");
  magnifierEl.id = MAGNIFIER_ID;

  Object.assign(magnifierEl.style, {
    position: "fixed",
    pointerEvents: "none",
    zIndex: "2147483647",
    padding: "10px 14px",
    borderRadius: "8px",
    background: "#000",
    color: "#fff",
    fontSize: "24px",
    fontWeight: "600",
    lineHeight: "1.4",
    boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
    display: "none",
    maxWidth: "420px",
    minWidth: "180px",
    width: "max-content",
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  });

  document.body.appendChild(magnifierEl);
}

function hideMagnifier() {
  if (!magnifierEl) return;

  magnifierEl.style.display = "none";
  magnifierEl.textContent = "";
}

function positionMagnifier(e: MouseEvent) {
  if (!magnifierEl || magnifierEl.style.display === "none") return;

  const padding = 12;
  const cursorGap = 18;

  const rect = magnifierEl.getBoundingClientRect();

  let x = e.clientX + cursorGap;
  let y = e.clientY - rect.height - cursorGap;

  const maxX = window.innerWidth - rect.width - padding;
  const minX = padding;

  if (x > maxX) {
    x = e.clientX - rect.width - cursorGap;
  }

  if (x < minX) {
    x = minX;
  }

  if (y < padding) {
    y = e.clientY + cursorGap;
  }

  const maxY = window.innerHeight - rect.height - padding;
  if (y > maxY) {
    y = maxY;
  }

  if (y < padding) {
    y = padding;
  }

  magnifierEl.style.left = `${x}px`;
  magnifierEl.style.top = `${y}px`;
}

function getEventElementTarget(e: Event): HTMLElement | null {
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];

  for (const node of path) {
    if (node instanceof HTMLElement) {
      if (node.id === MAGNIFIER_ID) continue;
      return node;
    }
  }

  return e.target instanceof HTMLElement ? e.target : null;
}

function getEligibleTextTarget(target: HTMLElement | null): HTMLElement | null {
  if (!target) return null;
  if (target.id === MAGNIFIER_ID) return null;

  const el = target.closest<HTMLElement>(ALLOWED_SELECTOR);
  if (!el) return null;

  const text = el.innerText?.trim();
  if (!text || text.length > 160) return null;

  return el;
}

function updateMagnifierFromMouseEvent(e: MouseEvent) {
  const rawTarget = getEventElementTarget(e);
  const textTarget = getEligibleTextTarget(rawTarget);

  if (!textTarget || !magnifierEl) {
    hideMagnifier();
    return;
  }

  magnifierEl.textContent = textTarget.innerText.trim();
  magnifierEl.style.display = "block";

  positionMagnifier(e);
}

export function enableContentMagnifier() {
  if (docMouseMoveHandler || docMouseOverHandler) return;

  createMagnifier();

  docMouseMoveHandler = (evt: Event) => {
    if (!(evt instanceof MouseEvent)) return;
    positionMagnifier(evt);
  };

  docMouseOverHandler = (evt: Event) => {
    if (!(evt instanceof MouseEvent)) return;
    updateMagnifierFromMouseEvent(evt);
  };

  document.addEventListener("mousemove", docMouseMoveHandler, true);
  document.addEventListener("mouseover", docMouseOverHandler, true);

  const shadowRoot = getWidgetShadowRoot();
  if (shadowRoot) {
    widgetMouseMoveHandler = (evt: Event) => {
      if (!(evt instanceof MouseEvent)) return;
      positionMagnifier(evt);
    };

    widgetMouseOverHandler = (evt: Event) => {
      if (!(evt instanceof MouseEvent)) return;
      updateMagnifierFromMouseEvent(evt);
    };

    shadowRoot.addEventListener("mousemove", widgetMouseMoveHandler, true);
    shadowRoot.addEventListener("mouseover", widgetMouseOverHandler, true);
  }
}

export function disableContentMagnifier() {
  if (docMouseMoveHandler) {
    document.removeEventListener("mousemove", docMouseMoveHandler, true);
    docMouseMoveHandler = null;
  }

  if (docMouseOverHandler) {
    document.removeEventListener("mouseover", docMouseOverHandler, true);
    docMouseOverHandler = null;
  }

  const shadowRoot = getWidgetShadowRoot();

  if (shadowRoot && widgetMouseMoveHandler) {
    shadowRoot.removeEventListener("mousemove", widgetMouseMoveHandler, true);
    widgetMouseMoveHandler = null;
  }

  if (shadowRoot && widgetMouseOverHandler) {
    shadowRoot.removeEventListener("mouseover", widgetMouseOverHandler, true);
    widgetMouseOverHandler = null;
  }

  if (magnifierEl) {
    magnifierEl.remove();
    magnifierEl = null;
  }
}
